import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import CircularProgress from '@mui/material/CircularProgress';
import Backdrop from '@mui/material/Backdrop';
import LinearProgress from '@mui/material/LinearProgress';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { useForm } from '../../../pages/General/useForm';
import Loader from '../../../pages/General/Loader';

import { fetchProfile, updateProfile } from './profileSlice';

// Reusable standard styling dictionary configuring standard outlined enterprise inputs
const enterpriseProfileInputStyles = {
    width: '100%',
    '& .MuiOutlinedInput-root': {
        borderRadius: '10px',
        backgroundColor: '#FFFFFF',
        transition: 'all 0.2s ease-in-out',
        border: '1px solid #E2E8F0',
        '& fieldset': { border: 'none' },
        '&:hover': {
            borderColor: '#CBD5E1',
            boxShadow: '0 2px 8px rgba(0,0,0,0.02)'
        },
        '&.Mui-focused': {
            borderColor: '#1976d2', // --- CORPORATE IDENTITY BRAND BLUE ---
            boxShadow: '0 0 0 3px rgba(25, 118, 210, 0.08)'
        },
        '&.Mui-disabled': {
            backgroundColor: '#F8FAFC',
            borderColor: '#E2E8F0',
            color: '#64748B'
        }
    },
    '& .MuiInputLabel-outlined': {
        color: '#64748B',
        fontWeight: 500,
        transform: 'translate(14px, 10px) scale(1)',
        '&.Mui-focused, &.MuiInputLabel-shrink': {
            transform: 'translate(14px, -8px) scale(0.75)',
            color: '#1976d2',
            fontWeight: 600
        },
        '&.Mui-disabled': {
            color: '#94A3B8'
        }
    },
    '& input': {
        padding: '10px 14px !important',
        fontSize: '13.5px',
        color: '#1E293B',
        fontWeight: 500
    },
    '& .MuiFormHelperText-root': {
        mx: 0.5,
        mt: 0.75,
        fontWeight: 500
    }
};

const ProfilePage = () => {
    const dispatch = useDispatch();

    const profile = useSelector(state => state.profile);
    const [alert, setAlert] = useState(false);
    const [open, setOpen] = useState(false);
    const [checkFormSubmit, setCheckFormSubmit] = useState(false);

    const initialFValues = {
        name: '',
        phone: '',
    }

    useEffect(() => {
        // Only dispatch if profile data is missing
        if (!profile?.profile || Object.keys(profile.profile).length === 0) {
            dispatch(fetchProfile());
        }
    }, [dispatch]);

    useEffect(() => {
        if (profile) {
            setValues({
                ...values,
                name: profile.profile.name,
                phone: profile.profile.phone,
                email: profile.profile.email,
            })
        }
        // 
        if (checkFormSubmit && profile && profile.message) {
            setAlert(true)
            setOpen(true)
        }
    }, [profile]);

    const validate = (fieldValues = values) => {
        let temp = { ...errors }
        if (fieldValues.name && fieldValues.name.length < 3)
            temp.name = "Name required minimum 3 characters"
        else {
            temp.name = ""
        }

        if (fieldValues.phone && fieldValues.phone.length < 10) {
            temp.phone = "The phone number minimum 10 digits"
        } else {
            temp.phone = ""
        }

        setErrors({
            ...temp
        })

        if (fieldValues == values)
            return Object.values(temp).every(x => x == "")
    }

    const handleClose = () => {
        setOpen(false)
    };

    const handleSubmit = e => {
        e.preventDefault()
        if (validate()) {
            setCheckFormSubmit(true)
            if (profile.profile.id) {
                dispatch(updateProfile(profile.profile.id,values))
            }
        }
    }

    const {
        values,
        setValues,
        errors,
        setErrors,
        handleInputChange
    } = useForm(initialFValues, validate, true);

    return (
        <Box className="sc--settings--card" sx={{ width: '100%', boxSizing: 'border-box', py: 1 }}>
            
            {/* Realtime Action Success / Error Feedback Alert Component */}
            {profile && profile.loading === false && alert && (
                <Snackbar
                    open={open}
                    anchorOrigin={{
                        vertical: "top",
                        horizontal: "right"
                    }}
                    autoHideDuration={6000}
                    onClose={handleClose}
                    sx={{ mt: 2 }}
                >
                    <Alert
                        severity={profile.hasErrors == true ? "error" : "success"}
                        onClose={handleClose}
                        variant="filled"
                        sx={{ 
                            width: '100%', 
                            borderRadius: '12px',
                            fontWeight: 600,
                            boxShadow: '0 10px 15px -3px rgba(0,0,0,0.05)',
                            fontSize: '13px',
                            alignItems: 'center'
                        }}
                    >
                        {profile.profile.message}
                    </Alert>
                </Snackbar>
            )}

            {/* Main Interactive Configuration Panel Workspace Card */}
            {profile && profile.loading === false && (
                <Card 
                    className="sc--cardhead--details"
                    sx={{ 
                        width: '100%',
                        maxWidth: 540,
                        mx: 'auto',
                        backgroundColor: '#FFFFFF',
                        border: '1px solid #E2E8F0',
                        borderRadius: '16px',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.02)',
                        overflow: 'hidden'
                    }}
                >
                    <CardHeader 
                        title={
                            <Typography variant="h6" component="h1" sx={{ fontWeight: 800, color: '#0F172A', fontSize: '16px', m: 0 }}>
                                Profile
                            </Typography>
                        } 
                        sx={{ p: 2.5, backgroundColor: '#FFFFFF' }}
                    />
                    
                    {profile && profile.loading === true && (
                        <LinearProgress sx={{ height: '3px', backgroundColor: '#E2E8F0', '& .MuiLinearProgress-bar': { backgroundColor: '#1976d2' } }} />
                    )}
                    
                    <Divider sx={{ borderColor: '#E2E8F0' }} />
                    
                    <CardContent sx={{ p: 4, boxSizing: 'border-box' }} component="form" onSubmit={handleSubmit} noValidate>
                        <Stack spacing={3.5}>
                            
                            {/* 1. Profile Account Full Name Entry Module Field */}
                            <Box className="sc--form--center" sx={{ width: '100%', minHeight: '44px' }}>
                                <TextField
                                    label='Name'
                                    size='small'
                                    name="name"
                                    value={values.name ? values.name : ""}
                                    variant="outlined"
                                    inputProps={{ maxLength: 25 }}
                                    onChange={handleInputChange}
                                    error={!!errors.name}
                                    helperText={errors.name}
                                    sx={enterpriseProfileInputStyles}
                                />
                            </Box>

                            {/* 2. Platform Locked Login Identifier Module Field (Read Only) */}
                            <Box className="sc--form--center" sx={{ width: '100%', minHeight: '44px' }}>
                                <TextField
                                    label='Email'
                                    size='small'
                                    name="email"
                                    disabled
                                    value={profile.profile.email ? profile.profile.email : ""}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    variant="outlined"
                                    sx={enterpriseProfileInputStyles}
                                />
                            </Box>

                            {/* 3. Operational Telecom Coordinates Entry Module Field */}
                            <Box className="sc--form--center" sx={{ width: '100%', minHeight: '44px' }}>
                                <TextField
                                    label="Phone"
                                    size="small"
                                    name="phone"
                                    value={values.phone || ""}
                                    onChange={(e) => {
                                        const filtered = e.target.value.replace(/[^0-9()+\-\s]/g, "");
                                        handleInputChange({
                                        target: {
                                            name: e.target.name,
                                            value: filtered,
                                        },
                                        });
                                    }}
                                    error={!!errors.phone}
                                    helperText={errors.phone}
                                    variant="outlined"
                                    inputProps={{ maxLength: 15 }}
                                    onKeyDown={(e) => {
                                        const allowedKeys = [
                                        "Backspace", "ArrowLeft", "ArrowRight", "Delete", "Tab",
                                        "+", "(", ")", "-", " ", // allow symbols
                                        ];

                                        // Allow digits and allowed symbols
                                        if (
                                        !/[0-9]/.test(e.key) &&
                                        !allowedKeys.includes(e.key)
                                        ) {
                                        e.preventDefault();
                                        }
                                    }}
                                    sx={enterpriseProfileInputStyles}
                                />
                            </Box>

                            {/* 4. Action Execution Processing Element Trigger Bar Footer */}
                            <Box className="sc--form--center" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', pt: 1, borderTop: '1px solid #E2E8F0' }}>
                                <Button 
                                    variant="contained" 
                                    type="submit"
                                    sx={{
                                        textTransform: 'none',
                                        borderRadius: '10px',
                                        fontWeight: 700,
                                        fontSize: '13.5px',
                                        height: '38px',
                                        px: 4,
                                        boxShadow: 'none',
                                        backgroundColor: '#1976d2',
                                        color: '#FFFFFF',
                                        '&:hover': { backgroundColor: '#115293', boxShadow: 'none' }
                                    }}
                                >
                                    Save Profile
                                </Button> 
                            </Box>
                        </Stack>
                    </CardContent>
                </Card>
            )}
        </Box>
    )
}

export default ProfilePage;